// Shared helpers for preserving unit/apartment numbers in address entry.
//
// The GraphQL schema has no unit column, so the unit has to survive inside
// `addressoneline`. Google already writes it there in the local idiom, and
// the form varies a lot by country:
//
//   CA  "50 Carabob Ct #714, Scarborough, ON M1T 3L9, Canada"
//   CA  "714-50 Carabob Ct, Scarborough, ON, Canada"
//   UK  "flat 3, 12 Baker St, London W1U 3HJ, UK"
//   AU  "5/12 Pitt St, Parramatta NSW 2150, Australia"
//   AU  "unit 5/12 Smith St, Rozelle NSW 2039, Australia"
//   US  "12 Oak Ave Apt 4B, Austin, TX 78701, USA"
//
// Two rules keep this honest:
//   1. Whatever `splitUnitFromAddress` can find, `addressContainsUnit` must
//      agree with — they share one implementation, so the write side can
//      never dedupe against a form the read side fails to recover.
//   2. When Google already supplied the unit we leave its string alone; we
//      only compose one ourselves for a manually typed unit.

const DESIGNATORS = [
    "unit",
    "apartment",
    "apt",
    "suite",
    "ste",
    "flat",
    "level",
    "studio",
    "villa",
    "penthouse",
    "shop",
    "room",
    "rm",
    "ph"
];

const DESIG = `(?:${DESIGNATORS.join("|")})`;

// A unit value must carry a digit (714, 4B, B12, PH2) or be a lone letter
// (UK "Flat A"). Requiring that keeps designator lookalikes out — the Quebec
// place name "Ste-Foy" has no digit after "Ste", so it is never read as a
// suite, and "Flatbush Ave" never matches because \b rejects it first.
const VALUE = String.raw`\d{1,6}[A-Za-z]?|[A-Za-z]{1,2}\d{1,5}[A-Za-z]?|[A-Za-z]`;

// street number on the far side of a "5/12" or "714-50" pair
const CIVIC = String.raw`\d{1,6}[A-Za-z]?`;

// "Unit 5" / "#5" / "apt. 5" — a designator followed by its value
const MARKER = `(?:${DESIG}\\b\\.?\\s*#?\\s*|#\\s*)`;

const RE = {
    // "flat 3, 12 Baker St…"  |  "#100 2570 Southvale…"
    leading: new RegExp(`^\\s*${MARKER}(${VALUE})\\s*(?:,\\s*|\\s+)`, "i"),
    // "5/12 Pitt St…"  |  "unit 5/12 Smith St…"   (AU, NZ, Scotland)
    slash: new RegExp(
        `^\\s*(?:${DESIG}\\b\\.?\\s*)?(${VALUE})\\s*/\\s*(${CIVIC})(?=[\\s,]|$)`,
        "i"
    ),
    // "714-50 Carabob Ct…"   (Canada Post style; never applied to US addresses)
    hyphen: new RegExp(`^\\s*(${VALUE})\\s*-\\s*(${CIVIC})(?=[\\s,]|$)`, "i"),
    // "50 Carabob Ct #714, …"  |  "…Crescent unit 100, …"  |  "…, Unit 100, …"
    inline: new RegExp(`[,\\s]+${MARKER}(${VALUE})(?![\\w-])`, "i"),
    designatorPrefix: new RegExp(`^${DESIG}\\b\\.?\\s*`, "i"),
    usa: /,\s*(?:usa|u\.s\.a\.|united states(?: of america)?)\.?\s*$/i
};

const tidy = (s) =>
    (s || "")
        .replace(/\s*,\s*/g, ", ")
        .replace(/(?:,\s*){2,}/g, ", ")
        .replace(/\s{2,}/g, " ")
        .replace(/^[\s,]+|[\s,]+$/g, "");

/**
 * Strip designators and punctuation from a raw unit.
 * "Unit 100" -> "100", "flat 3" -> "3", "#4B" -> "4B"
 *
 * Google embeds the designator inside its `subpremise` component
 * ("flat 3", "unit 5"), so this runs on that value too.
 */
export function normalizeUnit(raw) {
    if (!raw || typeof raw !== "string") return "";
    let v = raw.trim();
    v = v.replace(RE.designatorPrefix, "");
    v = v.replace(/^#\s*/, "");
    v = v.replace(/^[\s,.-]+|[\s,.-]+$/g, "");
    return v;
}

/**
 * Pull the unit out of a one-line address.
 * Returns { unit, baseAddress } — `unit` is "" when none is present.
 *
 * `options.localeSource` supplies the country context when the string being
 * parsed doesn't carry it (e.g. half-typed input); it decides whether the
 * Canada-Post hyphen form is allowed, since a leading "112-04" is an
 * indivisible house number in Queens, NY.
 */
export function splitUnitFromAddress(addressOneLine, options = {}) {
    if (!addressOneLine || typeof addressOneLine !== "string") {
        return { unit: "", baseAddress: addressOneLine || "" };
    }

    const localeSource = options.localeSource || addressOneLine;
    const allowHyphen = !RE.usa.test(localeSource);

    // 1. leading designator — "flat 3, 12 Baker St…"
    let m = addressOneLine.match(RE.leading);
    if (m) {
        return {
            unit: normalizeUnit(m[1]),
            baseAddress: tidy(addressOneLine.slice(m[0].length))
        };
    }

    // 2. slash pair — "5/12 Pitt St…" / "unit 5/12 Smith St…"
    m = addressOneLine.match(RE.slash);
    if (m) {
        return {
            unit: normalizeUnit(m[1]),
            baseAddress: tidy(m[2] + addressOneLine.slice(m[0].length))
        };
    }

    // 3. hyphen pair — "714-50 Carabob Ct…" (not US)
    if (allowHyphen) {
        m = addressOneLine.match(RE.hyphen);
        if (m) {
            return {
                unit: normalizeUnit(m[1]),
                baseAddress: tidy(m[2] + addressOneLine.slice(m[0].length))
            };
        }
    }

    // 4. designator anywhere else — "50 Carabob Ct #714, …", ", Unit 100, …"
    m = addressOneLine.match(RE.inline);
    if (m) {
        return {
            unit: normalizeUnit(m[1]),
            baseAddress: tidy(
                addressOneLine.slice(0, m.index) +
                    ", " +
                    addressOneLine.slice(m.index + m[0].length)
            )
        };
    }

    return { unit: "", baseAddress: addressOneLine };
}

/**
 * True when `addressOneLine` already carries this exact unit, in any of the
 * forms above. Shares `splitUnitFromAddress`, so the two can never disagree.
 */
export function addressContainsUnit(unit, addressOneLine) {
    const u = normalizeUnit(unit);
    if (!u || !addressOneLine) return false;
    const found = splitUnitFromAddress(addressOneLine).unit;
    return !!found && found.toLowerCase() === u.toLowerCase();
}

/** Append "#<unit>" to the street segment of a unit-free address. */
function appendUnit(unit, baseAddress) {
    const segments = baseAddress.split(",");
    // on the street segment, not as a new one: the provider SMS truncates
    // with addrOneLine.split(",").slice(0, 2), and an extra segment there
    // would push the city out of the message
    segments[0] = `${segments[0].trim()} #${unit}`;
    return segments.map((s) => s.trim()).join(", ");
}

/**
 * Put `unit` into `baseAddressOneLine`, preserving Google's own formatting
 * when it already says the same thing.
 *
 * - empty unit            -> address returned untouched (never destructive)
 * - same unit present     -> untouched, keeping the local idiom
 * - different unit present-> the old one is replaced
 * - no unit present       -> "#<unit>" appended to the street segment
 */
export function combineUnitWithAddress(unit, baseAddressOneLine) {
    const u = normalizeUnit(unit);
    if (!baseAddressOneLine) return baseAddressOneLine || "";
    if (!u) return baseAddressOneLine;

    const { unit: existing, baseAddress } =
        splitUnitFromAddress(baseAddressOneLine);
    if (existing && existing.toLowerCase() === u.toLowerCase()) {
        return baseAddressOneLine;
    }
    return appendUnit(u, existing ? baseAddress : baseAddressOneLine);
}

/**
 * Set the unit to exactly `unit`, dropping whatever was there before —
 * including when `unit` is empty, which clears it. Use this when the user
 * edits the Unit field; use combineUnitWithAddress when reconciling an
 * address that Google just returned.
 */
export function setUnitOnAddress(unit, addressOneLine) {
    if (!addressOneLine) return addressOneLine || "";
    const u = normalizeUnit(unit);
    const { baseAddress } = splitUnitFromAddress(addressOneLine);
    return u ? appendUnit(u, baseAddress) : baseAddress;
}

/**
 * Best-effort read of a unit the user typed into the autocomplete box before
 * Google's canonical address replaced it. `selectedDescription` is only used
 * for country context (see splitUnitFromAddress).
 */
export function extractTypedUnit(typedInput, selectedDescription) {
    if (!typedInput || typeof typedInput !== "string") return "";
    const { unit } = splitUnitFromAddress(typedInput, {
        localeSource: selectedDescription || typedInput
    });
    return unit;
}
